/**
 * "What Changed Today" — the signature vertical summary.
 * Modern card treatment: 2-col grid of vertical panels, each with a
 * top story + secondary bullets.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { npApi } from "./api";
import { timeAgo } from "./theme";

const GROUPS = [
  { key: "aviation",    label: "Aviation",             color: "from-sky-500 to-blue-600" },
  { key: "agriculture", label: "Agriculture",          color: "from-emerald-500 to-green-600" },
  { key: "travel",      label: "Travel",               color: "from-violet-500 to-purple-600" },
  { key: "food",        label: "Food",                 color: "from-amber-500 to-orange-600" },
  { key: "nature",      label: "Nature & Climate",     color: "from-green-500 to-emerald-700" },
  { key: "health",      label: "Health",               color: "from-rose-500 to-red-600" },
  { key: "government",  label: "Government & Politics", color: "from-slate-500 to-slate-700" },
  { key: "business",    label: "Business",             color: "from-yellow-500 to-amber-600" },
];

function isTodayIsh(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Date.now() - t < 36 * 3600 * 1000; // last 36h
}

export default function NewsPulseWhatChanged() {
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    npApi.latest({ limit: 100 })
      .then((r) => { if (!c) setLatest(r); })
      .catch(() => { if (!c) setLatest([]); })
      .finally(() => { if (!c) setLoading(false); });
    return () => { c = true; };
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const g of GROUPS) out[g.key] = [];
    for (const e of latest) {
      if (!isTodayIsh(e.last_updated_at || e.occurred_at)) continue;
      for (const cat of e.categories || []) {
        const top = cat.split("/")[0];
        if (out[top]) {
          out[top].push(e);
          break;
        }
      }
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => (b.importance || 0) - (a.importance || 0));
    }
    return out;
  }, [latest]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-10" data-testid="newspulse-what-changed">
      <div className="pb-6 mb-8 border-b border-slate-200">
        <div className="text-xs uppercase tracking-widest text-slate-500">Signature Report</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">What changed today</h1>
        <p className="text-slate-600 mt-3 text-base max-w-2xl">
          The highest-impact event across each vertical in the last 36 hours,
          drawn from the canonical event graph.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="wc-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white border border-slate-200 rounded-xl p-5">
              <div className="h-4 w-20 bg-slate-200 rounded" />
              <div className="h-6 w-3/4 bg-slate-200 rounded mt-4" />
              <div className="h-4 w-full bg-slate-200 rounded mt-3" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {GROUPS.map((g) => {
            const top = grouped[g.key]?.[0];
            const rest = (grouped[g.key] || []).slice(1, 4);
            return (
              <section
                key={g.key}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                data-testid={`wc-group-${g.key}`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest text-white bg-gradient-to-r ${g.color}`}>
                    {g.label}
                  </span>
                  <Link to={`/topic/${g.key}`} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5">
                    All <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                {top ? (
                  <>
                    <Link
                      to={`/event/${top.event_id}`}
                      className="block group"
                      data-testid={`wc-top-${g.key}`}
                    >
                      <h3 className="text-lg font-bold leading-snug text-slate-900 group-hover:text-blue-700 transition-colors">
                        {top.headline}
                      </h3>
                    </Link>
                    {top.summary && (
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">
                        {top.summary}
                      </p>
                    )}
                    <div className="mt-2 text-[11px] text-slate-500">
                      {timeAgo(top.last_updated_at)} · importance {top.importance}
                    </div>
                    {rest.length > 0 && (
                      <ul className="mt-3 pt-3 border-t border-slate-100 text-sm space-y-1.5">
                        {rest.map((r) => (
                          <li key={r.event_id}>
                            <Link
                              to={`/event/${r.event_id}`}
                              className="text-slate-700 hover:text-blue-700 flex gap-2"
                              data-testid={`wc-rest-${r.event_id}`}
                            >
                              <span className="text-slate-400 shrink-0">→</span>
                              <span className="line-clamp-2">{r.headline}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400 italic">No new events in the last 36 hours.</p>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
